import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatPredictionDataVersion } from '@/utils/versionTag';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Button,
  Select,
  Space,
  Spin,
  Table,
  Progress,
  Tooltip,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ResizableTitle, MiddleEllipsis, useResizableColumns } from '@components/ResizableTable';
import { renderAlgorithmVersionCell } from '@components/AlgorithmVersionCell';
import { useVersionTrace } from '@components/VersionTraceModal/useVersionTrace';
import { JobStatusTag } from '@components/StatusTag';
import DailySummaryCard, {
  type DailySummary,
} from '@components/DailySummaryCard';
import {
  compareDailyJobs,
  getDailyJobList,
  getDailyJobOptions,
  getDailyLatestJobs,
  getDailyPreviousJob,
  type DailyCompare,
  type DailyJobBrief,
  type JobItem,
  type SimType,
} from '@/apis/simulation';
import './index.less';
import { completionPercent } from '@/common/progress';

const { Title } = Typography;

/** 默认异常率阈值（千分比 ‰）。后端无该字段，前端按规则自定。 */
const ANOMALY_THRESHOLD: number = 1;

/** 统一展示文案：``{jobId}_{jobName}``，``jobName`` 缺失时退化为 ``{jobId}``。 */
const formatJobLabel = (jobId: number, jobName?: string | null): string =>
  jobName ? `${jobId}_${jobName}` : `${jobId}`;

/** Job options select 的 label/value 转换 */
const toSelectOptions = (
  options: DailyJobBrief[],
  excludeJobId?: number,
) =>
  options
    .filter((o) => excludeJobId === undefined || o.jobId !== excludeJobId)
    .map((o) => ({
      value: o.jobId,
      label: formatJobLabel(o.jobId, o.jobName),
    }));

/** 把 compare 接口结果转成 DailySummary */
const toSummary = (
  compareJobName: string,
  cmp: DailyCompare,
  todayJobName?: string,
): DailySummary => ({
  todayJobName,
  compareJobName,
  currentPassRate: cmp.today.passRate,
  comparePassRate: cmp.compare.passRate,
  anomalyRate: cmp.today.anomalyRate,
  anomalyThreshold: ANOMALY_THRESHOLD,
  newSuccessSceneCount: cmp.newSuccessSceneCount,
  newFailSceneCount: cmp.newFailSceneCount,
});

/** 在 options 中按 jobId 找 ``{jobId}_{jobName}``，找不到则退化为 ``{jobId}``。 */
const findJobLabel = (options: DailyJobBrief[], jobId: number): string => {
  const hit = options.find((o) => o.jobId === jobId);
  return formatJobLabel(jobId, hit?.jobName);
};

// ---------------- 顶部 SummaryCard ----------------

interface TopSummarySectionProps {
  options: DailyJobBrief[];
  todayJob: DailyJobBrief | null;
  defaultCompareJobId: number | null;
}

function TopSummarySection({
  options,
  todayJob,
  defaultCompareJobId,
}: TopSummarySectionProps) {
  const navigate = useNavigate();
  const [compareJobId, setCompareJobId] = useState<number | undefined>(
    defaultCompareJobId ?? undefined,
  );
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setCompareJobId(defaultCompareJobId ?? undefined);
  }, [defaultCompareJobId]);

  useEffect(() => {
    if (!todayJob || !compareJobId) {
      setSummary(null);
      return;
    }
    let mounted = true;
    setLoading(true);
    compareDailyJobs(todayJob.jobId, compareJobId)
      .then((cmp) => {
        if (!mounted) return;
        if (!cmp) {
          setSummary(null);
          return;
        }
        setSummary(
          toSummary(
            findJobLabel(options, compareJobId),
            cmp,
            formatJobLabel(todayJob.jobId, todayJob.jobName),
          ),
        );
      })
      .catch((err) => {
        console.error('加载顶部对比指标失败:', err);
        if (mounted) setSummary(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [todayJob, compareJobId, options]);

  if (!todayJob) {
    return (
      <div className="daily-summary-empty">
        暂无 Daily 仿真 Job
      </div>
    );
  }

  const selectOptions = toSelectOptions(options, todayJob.jobId);
  const canReset =
    defaultCompareJobId !== null && compareJobId !== defaultCompareJobId;
  const compareSlot = (
    <Space size={4}>
      <Select
        size="small"
        value={compareJobId}
        options={selectOptions}
        onChange={(v) => setCompareJobId(v)}
        placeholder="请选择对比 Job"
        style={{ minWidth: 200 }}
        showSearch
        optionFilterProp="label"
        loading={loading}
      />
      <Tooltip title="重置为默认对比 Job">
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          disabled={!canReset}
          onClick={() => setCompareJobId(defaultCompareJobId ?? undefined)}
        />
      </Tooltip>
    </Space>
  );

  // 没有对比 Job 时仍然渲染卡片，仅指标全 0
  const renderSummary: DailySummary =
    summary || {
      todayJobName: formatJobLabel(todayJob.jobId, todayJob.jobName),
      compareJobName: '-',
      currentPassRate: 0,
      comparePassRate: 0,
      anomalyRate: 0,
      anomalyThreshold: ANOMALY_THRESHOLD,
      newSuccessSceneCount: 0,
      newFailSceneCount: 0,
    };

  const goTaskList = (mode: 'new_success' | 'new_fail') => {
    if (!compareJobId) return;
    navigate(
      `/job-tasks/${todayJob.jobId}?compareJobId=${compareJobId}&diffMode=${mode}`,
    );
  };

  return (
    <Spin spinning={loading}>
      <DailySummaryCard
        summary={renderSummary}
        compareSlot={compareSlot}
        onClickNewSuccess={
          summary && summary.newSuccessSceneCount > 0
            ? () => goTaskList('new_success')
            : undefined
        }
        onClickNewFail={
          summary && summary.newFailSceneCount > 0
            ? () => goTaskList('new_fail')
            : undefined
        }
      />
    </Spin>
  );
}

// ---------------- 展开行 SummaryCard ----------------

interface RowExpandedSummaryProps {
  todayJob: JobItem;
  options: DailyJobBrief[];
  /** 归属仿真类型；用于「前置 daily Job」查询按 sim_type 过滤 */
  simType?: SimType;
}

function RowExpandedSummary({ todayJob, options, simType }: RowExpandedSummaryProps) {
  const navigate = useNavigate();
  const [compareJobId, setCompareJobId] = useState<number | undefined>(undefined);
  const [defaultCompareJobId, setDefaultCompareJobId] = useState<number | undefined>(undefined);
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [defaultLoaded, setDefaultLoaded] = useState<boolean>(false);

  // 默认对比 Job：当前 Job 之前最近一条 daily Job（按 sim_type 过滤，确保不跨类别）
  useEffect(() => {
    let mounted = true;
    getDailyPreviousJob(todayJob.jobId, simType)
      .then((prev) => {
        if (!mounted) return;
        setCompareJobId(prev?.jobId);
        setDefaultCompareJobId(prev?.jobId);
      })
      .catch((err) => {
        console.error('查询前置 daily Job 失败:', err);
      })
      .finally(() => {
        if (mounted) setDefaultLoaded(true);
      });
    return () => {
      mounted = false;
    };
  }, [todayJob.jobId, simType]);

  // compareJobId 变化时刷新指标
  useEffect(() => {
    if (!compareJobId) {
      setSummary(null);
      return;
    }
    let mounted = true;
    setLoading(true);
    compareDailyJobs(todayJob.jobId, compareJobId)
      .then((cmp) => {
        if (!mounted) return;
        if (!cmp) {
          setSummary(null);
          return;
        }
        setSummary(toSummary(findJobLabel(options, compareJobId), cmp));
      })
      .catch((err) => {
        console.error('展开行加载对比指标失败:', err);
        if (mounted) setSummary(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [todayJob.jobId, compareJobId, options]);

  const selectOptions = toSelectOptions(options, todayJob.jobId);
  const canReset =
    defaultCompareJobId !== undefined && compareJobId !== defaultCompareJobId;
  const compareSlot = (
    <Space size={4}>
      <Select
        size="small"
        value={compareJobId}
        options={selectOptions}
        onChange={(v) => setCompareJobId(v)}
        placeholder={defaultLoaded ? '暂无前置 Job，可手动选择' : '加载中...'}
        style={{ minWidth: 200 }}
        showSearch
        optionFilterProp="label"
        loading={loading}
      />
      <Tooltip title="重置为前一个 Daily Job">
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          disabled={!canReset}
          onClick={() => setCompareJobId(defaultCompareJobId)}
        />
      </Tooltip>
    </Space>
  );

  const renderSummary: DailySummary = summary || {
    todayJobName: undefined,
    compareJobName: '-',
    currentPassRate: 0,
    comparePassRate: 0,
    anomalyRate: 0,
    anomalyThreshold: ANOMALY_THRESHOLD,
    newSuccessSceneCount: 0,
    newFailSceneCount: 0,
  };

  const goTaskList = (mode: 'new_success' | 'new_fail') => {
    if (!compareJobId) return;
    navigate(
      `/job-tasks/${todayJob.jobId}?compareJobId=${compareJobId}&diffMode=${mode}`,
    );
  };

  return (
    <div className="daily-expanded-wrapper">
      <Spin spinning={loading}>
        <DailySummaryCard
          summary={renderSummary}
          title={null}
          compareSlot={compareSlot}
          onClickNewSuccess={
            summary && summary.newSuccessSceneCount > 0
              ? () => goTaskList('new_success')
              : undefined
          }
          onClickNewFail={
            summary && summary.newFailSceneCount > 0
              ? () => goTaskList('new_fail')
              : undefined
          }
        />
      </Spin>
    </div>
  );
}

// ---------------- 页面主体 ----------------

export default function DailySimulationPage() {
  const navigate = useNavigate();
  const { onTrace, modal: versionTraceModal } = useVersionTrace();
  const [searchParams] = useSearchParams();
  // sim_type：与 JobList 一样按 query 判分。感知的 Daily 后端未接入，先给占位。
  const simType = useMemo(() => {
    const v = searchParams.get('sim_type');
    return v === 'perception' ? 'perception' : 'planning';
  }, [searchParams]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  // 顶部 SummaryCard：今日 Job + 默认对比 Job + Job 候选项
  const [todayJob, setTodayJob] = useState<DailyJobBrief | null>(null);
  const [defaultCompareJobId, setDefaultCompareJobId] = useState<number | null>(null);
  const [jobOptions, setJobOptions] = useState<DailyJobBrief[]>([]);
  const [headerLoaded, setHeaderLoaded] = useState<boolean>(false);

  // Job 列表
  const [data, setData] = useState<JobItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 加载顶部 SummaryCard 所需 latest + options
  const loadHeader = useCallback(async () => {
    try {
      const [latest, options] = await Promise.all([
        getDailyLatestJobs(simType),
        getDailyJobOptions({ limit: 200, simType }),
      ]);
      setTodayJob(latest.today);
      setDefaultCompareJobId(latest.compare?.jobId ?? null);
      setJobOptions(options);
    } catch (err) {
      console.error('加载 daily 顶部数据失败:', err);
    } finally {
      setHeaderLoaded(true);
    }
  }, [simType]);

  const loadJobList = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getDailyJobList({ current: page, pageSize, simType });
      setData(resp.tasks);
      setTotal(resp.total);
    } catch (err) {
      console.error('加载 daily Job 列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, simType]);

  useEffect(() => {
    loadHeader();
  }, [loadHeader]);

  useEffect(() => {
    loadJobList();
  }, [loadJobList]);

  const { columnWidths, resizeHeaderCell } = useResizableColumns({
    jobId: 80,
    jobName: 200,
    priorityName: 80,
    sourceName: 90,
    algorithmVersion: 160,
    predictionDataVersion: 160,
    sceneSetIds: 140,
    createTime: 160,
    endTime: 160,
    creator: 90,
    progress: 180,
    statusName: 100,
  });

  const columns: ColumnsType<JobItem> = useMemo(() => [
    {
      title: '任务ID',
      dataIndex: 'jobId',
      key: 'jobId',
      width: columnWidths.jobId,
      align: 'center',
      onHeaderCell: resizeHeaderCell('jobId'),
      render: (text) => text || '-',
    },
    {
      title: '任务名称',
      dataIndex: 'jobName',
      key: 'jobName',
      width: columnWidths.jobName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('jobName'),
      render: (text, record) => (
        <Link
          to={`/job-tasks/${record.jobId}`}
          style={{ color: '#5e6ad2', cursor: 'pointer', display: 'block' }}
        >
          <MiddleEllipsis text={text || '-'} />
        </Link>
      ),
    },
    {
      title: '优先级',
      dataIndex: 'priorityName',
      key: 'priorityName',
      width: columnWidths.priorityName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('priorityName'),
      render: (text) => text || '-',
    },
    {
      title: '数据源',
      dataIndex: 'sourceName',
      key: 'sourceName',
      width: columnWidths.sourceName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('sourceName'),
      render: (text) => text || '-',
    },
    {
      title: '算法版本',
      dataIndex: 'algorithmVersion',
      key: 'algorithmVersion',
      width: columnWidths.algorithmVersion,
      align: 'center',
      onHeaderCell: resizeHeaderCell('algorithmVersion'),
      render: (_text, record) => renderAlgorithmVersionCell(record, onTrace),
    },
    {
      // LPL 预测数据版本：建 Job 时冻结的实际生效值（default / 具体版本号）。
      title: '预测数据版本',
      dataIndex: 'predictionDataVersion',
      key: 'predictionDataVersion',
      width: columnWidths.predictionDataVersion,
      align: 'center',
      onHeaderCell: resizeHeaderCell('predictionDataVersion'),
      render: (text) => (text ? <MiddleEllipsis text={formatPredictionDataVersion(text)} /> : '-'),
    },
    {
      title: '场景集合',
      dataIndex: 'sceneSetIds',
      key: 'sceneSetIds',
      width: columnWidths.sceneSetIds,
      align: 'center',
      onHeaderCell: resizeHeaderCell('sceneSetIds'),
      render: (text) => (text ? <MiddleEllipsis text={text} /> : '-'),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: columnWidths.createTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('createTime'),
      render: (text) => {
        if (!text) return '-';
        try {
          return <MiddleEllipsis text={new Date(text).toLocaleString('zh-CN')} />;
        } catch {
          return text;
        }
      },
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      width: columnWidths.endTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('endTime'),
      render: (text) => {
        if (!text) return '-';
        try {
          return <MiddleEllipsis text={new Date(text).toLocaleString('zh-CN')} />;
        } catch {
          return text;
        }
      },
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      key: 'creator',
      width: columnWidths.creator,
      align: 'center',
      onHeaderCell: resizeHeaderCell('creator'),
      render: (text) => (text ? <MiddleEllipsis text={text} /> : '-'),
    },
    {
      title: '进度',
      key: 'progress',
      width: columnWidths.progress,
      align: 'center',
      onHeaderCell: resizeHeaderCell('progress'),
      render: (_, record) => {
        const { totalTaskNum, successTaskNum, failTaskNum } = record;
        if (!totalTaskNum) return '-';
        const completedNum = successTaskNum + failTaskNum;
        const percent = completionPercent(completedNum, totalTaskNum);
        const successPercent = completionPercent(successTaskNum, totalTaskNum);
        const status = failTaskNum > 0 ? 'exception' : percent === 100 ? 'success' : 'active';

        return (
          <Tooltip title={`成功: ${successTaskNum} / 失败: ${failTaskNum} / 总数: ${totalTaskNum}`}>
            <div style={{ minWidth: 120 }}>
              <Progress
                percent={percent}
                success={{ percent: successPercent }}
                size="small"
                status={status}
                format={() => `${completedNum}/${totalTaskNum}`}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'statusName',
      key: 'statusName',
      width: columnWidths.statusName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('statusName'),
      render: (_, record) => (
        <JobStatusTag status={record.status} statusName={record.statusName} />
      ),
    },
  ], [columnWidths, resizeHeaderCell, navigate, onTrace]);

  // PNC / 感知 Daily 复用同一套 UI；仅顶部标题按 sim_type 切文案，
  // 数据由后端按 sim_type 精确过滤（源=daily + task_type.sim_type=<simType>）。
  const pageTitle = simType === 'perception'
    ? '感知 Daily 仿真 Job 列表'
    : 'PNC Daily 仿真 Job 列表';

  return (
    <div className="daily-page">
      <div className="daily-page-header">
        <div className="daily-page-header-title">
          <Title level={5} style={{ margin: 0, lineHeight: 1.3 }}>{pageTitle}</Title>
        </div>
      </div>

      {headerLoaded && (
        <TopSummarySection
          options={jobOptions}
          todayJob={todayJob}
          defaultCompareJobId={defaultCompareJobId}
        />
      )}

      <div className="daily-table-card">
        <Table<JobItem>
          rowKey="jobId"
          dataSource={data}
          columns={columns}
          loading={loading}
          size="small"
          components={{ header: { cell: ResizableTitle } }}
          tableLayout="fixed"
          scroll={{ x: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '1000'],
            showQuickJumper: true,
            showTotal: (t) => `总共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as number[]),
            expandedRowRender: (record) => (
              <RowExpandedSummary todayJob={record} options={jobOptions} simType={simType} />
            ),
            rowExpandable: () => true,
          }}
        />
      </div>
      {versionTraceModal}
    </div>
  );
}
